import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Statistic, 
  Alert, 
  Typography, 
  Space, 
  Tag,
  Progress,
  List,
  Badge
} from 'antd';
import {
  DashboardOutlined,
  EnvironmentOutlined,
  MobileOutlined,
  FileImageOutlined,
  WifiOutlined,
  DisconnectOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import { useAuth, useCompanyScope } from '../hooks/usePermissions';
import { useResponsive } from '../hooks/useResponsive';
import { apiGetWithScope } from '../utils/api';

const { Title, Text } = Typography;

interface OperatorDashboardStats {
  totalRoutes: number;
  totalDevices: number;
  onlineDevices: number;
  offlineDevices: number;
  totalMedia: number;
  totalPlaylists: number;
  deviceUptime: number;
}

interface DeviceStatus {
  id: number;
  device_name: string;
  route_number: string;
  status: 'online' | 'offline';
  last_seen: string;
  location?: string;
}

export default function OperatorDashboard() {
  const { user, isOperator } = useAuth();
  const { scope, currentCompanyId } = useCompanyScope();
  const { isMobile } = useResponsive();
  
  const [stats, setStats] = useState<OperatorDashboardStats | null>(null);
  const [devices, setDevices] = useState<DeviceStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 운영자가 아닌 경우 접근 차단
  if (!isOperator || !user?.company_id) {
    return (
      <Alert
        message="운수사 운영자 전용 대시보드"
        description="이 페이지는 운수사 운영자만 접근할 수 있습니다."
        type="warning"
        showIcon
        style={{ margin: '24px' }}
      />
    );
  }

  useEffect(() => {
    loadOperatorDashboard();
    
    // 30초마다 자동 새로고침
    const interval = setInterval(loadOperatorDashboard, 30000);
    return () => clearInterval(interval);
  }, [currentCompanyId]);

  const loadOperatorDashboard = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!currentCompanyId) {
        throw new Error('운수사 정보를 찾을 수 없습니다.');
      }

      // 운수사별 통계 데이터 로드
      const [statsResponse, devicesResponse] = await Promise.all([
        apiGetWithScope('/api/dashboard/operator-stats', { companyId: currentCompanyId }),
        apiGetWithScope('/api/devices', { companyId: currentCompanyId })
      ]);

      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        if (statsData.success) {
          setStats(statsData.data);
        }
      }

      if (devicesResponse.ok) {
        const devicesData = await devicesResponse.json();
        if (devicesData.success) {
          setDevices(devicesData.data.slice(0, 10)); // 최근 10개만 표시
        }
      }

    } catch (error: any) {
      console.error('운영자 대시보드 로드 실패:', error);
      setError(error.message || '데이터를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const getDeviceStatusColor = (status: string) => {
    return status === 'online' ? '#52c41a' : '#ff4d4f';
  };

  const getUptimeColor = (uptime: number) => {
    if (uptime >= 95) return '#52c41a';
    if (uptime >= 80) return '#faad14';
    return '#ff4d4f';
  };

  return (
    <div style={{ padding: isMobile ? 16 : 24 }}>
      {/* 헤더 */}
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0, display: 'flex', alignItems: 'center' }}>
          <DashboardOutlined style={{ marginRight: 8 }} />
          {user.company_name} 대시보드
        </Title>
        <Text type="secondary">
          운수사 운영 현황을 실시간으로 모니터링할 수 있습니다.
        </Text>
      </div>

      {/* 에러 메시지 */}
      {error && (
        <Alert
          message="데이터 로드 실패"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}

      {/* 통계 카드 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={loading}>
            <Statistic
              title="운영 노선 수"
              value={stats?.totalRoutes || 0}
              prefix={<EnvironmentOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={loading}>
            <Statistic
              title="총 단말기 수"
              value={stats?.totalDevices || 0}
              prefix={<MobileOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={loading}>
            <Statistic
              title="온라인 단말기"
              value={stats?.onlineDevices || 0}
              prefix={<WifiOutlined style={{ color: '#52c41a' }} />}
              suffix={
                <span style={{ fontSize: '14px', color: '#666' }}>
                  / {stats?.totalDevices || 0}
                </span>
              }
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={loading}>
            <Statistic
              title="콘텐츠 수"
              value={stats?.totalMedia || 0}
              prefix={<FileImageOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* 시스템 상태 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={12}>
          <Card 
            title="시스템 가동률" 
            loading={loading}
            extra={
              <Tag color={getUptimeColor(stats?.deviceUptime || 0)}>
                {(stats?.deviceUptime || 0).toFixed(1)}%
              </Tag>
            }
          >
            <Progress
              percent={stats?.deviceUptime || 0}
              strokeColor={getUptimeColor(stats?.deviceUptime || 0)}
              showInfo={false}
              style={{ marginBottom: 16 }}
            />
            <Space direction="vertical" style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text>온라인 단말기:</Text>
                <Badge 
                  count={stats?.onlineDevices || 0} 
                  style={{ backgroundColor: '#52c41a' }} 
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text>오프라인 단말기:</Text>
                <Badge 
                  count={stats?.offlineDevices || 0} 
                  style={{ backgroundColor: '#ff4d4f' }} 
                />
              </div>
            </Space>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card 
            title="운영 현황" 
            loading={loading}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text>운영 노선:</Text>
                <Tag color="blue">{stats?.totalRoutes || 0}개</Tag>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text>플레이리스트:</Text>
                <Tag color="green">{stats?.totalPlaylists || 0}개</Tag>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text>총 콘텐츠:</Text>
                <Tag color="purple">{stats?.totalMedia || 0}개</Tag>
              </div>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* 단말기 상태 목록 */}
      <Card 
        title="단말기 실시간 상태" 
        loading={loading}
        extra={
          <Text type="secondary" style={{ fontSize: '12px' }}>
            최근 업데이트: {new Date().toLocaleTimeString()}
          </Text>
        }
      >
        <List
          dataSource={devices}
          renderItem={(device) => (
            <List.Item>
              <List.Item.Meta
                avatar={
                  device.status === 'online' 
                    ? <WifiOutlined style={{ color: '#52c41a', fontSize: 16 }} />
                    : <DisconnectOutlined style={{ color: '#ff4d4f', fontSize: 16 }} />
                }
                title={
                  <Space>
                    <Text strong>{device.device_name}</Text>
                    <Tag color={device.status === 'online' ? 'success' : 'error'}>
                      {device.status === 'online' ? '온라인' : '오프라인'}
                    </Tag>
                  </Space>
                }
                description={
                  <Space direction="vertical" size={2}>
                    <Text type="secondary">노선: {device.route_number}</Text>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      마지막 접속: {new Date(device.last_seen).toLocaleString()}
                    </Text>
                  </Space>
                }
              />
            </List.Item>
          )}
          locale={{
            emptyText: '등록된 단말기가 없습니다.'
          }}
        />
      </Card>
    </div>
  );
}